import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";

function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 36 }).map((_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.7,
    duration: 2.6 + Math.random() * 1.9,
    size: 6 + Math.random() * 7,
    rot: Math.random() * 360,
    color: ["#F97316", "#FB923C", "#FBBF24", "#16A34A", "#F5E9DA"][i % 5],
    round: Math.random() > 0.6,
  })), []);
  return (
    <div className="dk-confetti" aria-hidden data-testid="prove-complete-confetti">
      {pieces.map((p, i) => (
        <span key={i} className="dk-confetti-piece" style={{
          left: `${p.left}%`,
          width: `${p.size}px`,
          height: `${p.round ? p.size : p.size * 0.42}px`,
          background: p.color,
          borderRadius: p.round ? "9999px" : "2px",
          animationDelay: `${p.delay}s`,
          animationDuration: `${p.duration}s`,
          "--dk-rot": `${p.rot}deg`,
        }} />
      ))}
      <style>{`
        .dk-confetti{position:absolute;left:0;right:0;top:0;height:440px;overflow:hidden;pointer-events:none;z-index:20}
        .dk-confetti-piece{position:absolute;top:-24px;opacity:0;will-change:transform,opacity;animation-name:dkfall;animation-timing-function:cubic-bezier(.32,.62,.4,1);animation-iteration-count:1;animation-fill-mode:forwards}
        @keyframes dkfall{0%{opacity:0;transform:translateY(-16px) rotate(0)}12%{opacity:1}100%{opacity:0;transform:translateY(430px) rotate(var(--dk-rot))}}
        @media (prefers-reduced-motion: reduce){.dk-confetti{display:none}}
      `}</style>
    </div>
  );
}

export default function ProveComplete() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    api.get(`/workspaces/${id}`).then(({ data }) => setWs(data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => {
    if (!loading && ws) {
      setCelebrate(true);
      const t = setTimeout(() => setCelebrate(false), 5000);
      return () => clearTimeout(t);
    }
  }, [loading, ws]);

  if (loading) {
    return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-donkey-orange" /></div>;
  }

  const name = ws?.opportunity_name || "your opportunity";

  return (
    <div className="relative overflow-hidden mx-auto max-w-3xl px-5 sm:px-8 py-16" data-testid="prove-complete">
      {celebrate && <Confetti />}
      <div className="relative">
        <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full bg-donkey-orange/15 blur-3xl" aria-hidden />
        <div className="relative text-center">
          <DonkeyMascot size={110} className="mx-auto mb-5" />
          <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-donkey-green mb-3">
            <CheckCircle2 className="w-4 h-4" /> Prove complete
          </p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-donkey-warm">
            You've completed Prove.
          </h1>
          <p className="text-donkey-grey mt-4 text-base max-w-xl mx-auto leading-relaxed">
            You now have a business opportunity worth pursuing and a real validation starting point — grounded in your
            own circumstances, not guesswork. That's the hard part done honestly.
          </p>
        </div>
      </div>

      <div className="mt-10 card-donkey rounded-2xl p-6 sm:p-8 border-l-2 border-donkey-orange" data-testid="prove-complete-opportunity">
        <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">Your chosen opportunity</p>
        <h2 className="font-heading font-semibold text-xl text-donkey-warm">{name}</h2>
        {ws?.opportunity?.proposition && (
          <p className="text-sm text-donkey-grey mt-2 leading-relaxed">{ws.opportunity.proposition}</p>
        )}
      </div>

      {/* One clear primary action */}
      <div className="mt-8 flex flex-col items-center gap-4">
        <Button onClick={() => navigate(`/workspace/${id}`)}
          className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-bold rounded-full px-8 py-3.5 text-base shadow-[0_4px_20px_rgba(249,115,22,0.3)] hover:-translate-y-0.5 transition-all"
          data-testid="prove-complete-continue-btn">
          Keep working on {name.length > 28 ? "this opportunity" : name} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        {/* Optional next step — clearly optional, never pushy */}
        <div className="w-full mt-4 rounded-2xl border border-donkey-line bg-white/[0.02] p-5 text-center" data-testid="prove-complete-build">
          <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-donkey-grey mb-2">
            <Sparkles className="w-3.5 h-3.5 text-donkey-orange" /> Optional — only if you're ready
          </p>
          <p className="text-sm text-donkey-warm/90">
            When you decide to take this further, <span className="text-donkey-warm font-medium">Build</span> helps you shape the brand,
            offer and pricing. There's no rush and no obligation — Prove has already given you what you need to decide.
          </p>
          <Button variant="outline" onClick={() => navigate("/pricing")}
            className="mt-4 border-donkey-line bg-transparent text-donkey-warm hover:bg-white/5"
            data-testid="prove-complete-learn-build-btn">
            Learn about Build (optional)
          </Button>
        </div>

        <button onClick={() => navigate("/dashboard")} className="mt-2 text-sm text-donkey-grey hover:text-donkey-warm" data-testid="prove-complete-dashboard-btn">
          Back to your dashboard
        </button>
      </div>
    </div>
  );
}
